// src/components/upmode-agent/publish-confirm-card.tsx
//
// The point where something leaves Upmode.
//
// Source: handoff-advanced §1.3 and the "Publish Approval" region of
// `Upmode Agent - Polished.dc.html`.
//
// ── Everything below the surface is unchanged ──────────────────────────────
//
// The spec's own warning — "read this component before restyling; the real
// behaviour is richer than it looks" — is the reason this is a restyle and not
// a rebuild. All of it survives, verbatim:
//
//   * the two-part control per destination: an INCLUDE checkbox and a TAB.
//     Include is not the same as active. You can look at a destination you
//     have chosen not to send, and collapsing the two would remove that.
//   * "Destinations (N of M selected)"
//   * the edit toggle flipping every field between read-only and editable
//   * the per-destination fields: hashnode {title, content_markdown, tags} at
//     250 chars, wordpress {title, content_html, excerpt} at 200/300,
//     linkedin {text}
//   * LinkedIn's two limits, and they are two different OUTCOMES: over 1,300
//     the post publishes and gets truncated in the feed; over 3,000 it is
//     rejected. Two outcomes, so two tones — warning amber and gap coral —
//     rather than one colour for both.
//   * the four button labels
//
// ── The one layout trap ────────────────────────────────────────────────────
//
// The card is a direct child of the messages scroller, which is a flex column
// with `overflow-y: auto`. `overflow: hidden` on the card resolves its
// `min-height: auto` to 0, and the default `flex-shrink: 1` then crushes it to
// about two pixels. `flex-shrink: 0` is in `.upm-approve` and is load-bearing.

import React, { useState, useMemo } from "react";
import { AlertTriangle, Check, Edit3, Rocket, X } from "lucide-react";
import { PublishDestination } from "@/app/services/upmode-agent/upmode-agent-api";
import { Button, Checkbox, Icon, IconButton, TextInput, Textarea } from "../kit";
import { DEST_META, DestinationMark } from "@/app/components/shared/advanced-vocabulary";

export interface PendingPublish {
  tool_use_id: string;
  destinations: PublishDestination[];
  content: Record<string, Record<string, any>>;
  summary: string;
}

interface PublishConfirmCardProps {
  pending: PendingPublish;
  onConfirm: (selected: Array<{ destination: PublishDestination; content: Record<string, any> }>) => Promise<void>;
  onCancel: () => void;
  busy?: boolean;
}

/** LinkedIn's two thresholds, named once so the copy and the check agree. */
const LINKEDIN_SOFT = 1300;
const LINKEDIN_HARD = 3000;

export function PublishConfirmCard({ pending, onConfirm, onCancel, busy }: PublishConfirmCardProps) {
  const [edited, setEdited] = useState<Record<string, Record<string, any>>>(() => {
    const copy: Record<string, Record<string, any>> = {};
    for (const d of pending.destinations) copy[d] = { ...(pending.content[d] || {}) };
    return copy;
  });

  const [included, setIncluded] = useState<Record<string, boolean>>(() => {
    const copy: Record<string, boolean> = {};
    for (const d of pending.destinations) copy[d] = true;
    return copy;
  });

  const [activeTab, setActiveTab] = useState<PublishDestination>(pending.destinations[0]);
  const [editing, setEditing] = useState(false);

  const selectedCount = useMemo(
    () => pending.destinations.filter(d => included[d]).length,
    [pending.destinations, included],
  );

  const updateField = (dest: string, field: string, value: any) => {
    setEdited(prev => ({ ...prev, [dest]: { ...prev[dest], [field]: value } }));
  };

  const handlePublish = async () => {
    const selected = pending.destinations
      .filter(d => included[d])
      .map(d => ({ destination: d, content: edited[d] || {} }));
    if (!selected.length) return;
    await onConfirm(selected);
  };

  const cta = busy
    ? "Publishing…"
    : selectedCount === 0
      ? "Select at least one"
      : selectedCount === pending.destinations.length
        ? `Publish to all (${selectedCount})`
        : `Publish to ${selectedCount}`;

  return (
    <div className="upm-approve">
      <div className="upm-approve__rule" />
      <div className="upm-approve__inner">
        <div className="upm-approve__mark">
          <span className="upm-approve__rocket">
            <Icon icon={Rocket} size="sm" />
          </span>
          <span className="upm-adv-eyebrow upm-adv-eyebrow--brand">Waiting on you</span>
        </div>

        <div className="upm-approve__head">
          <h4 className="upm-approve__title">Ready to publish</h4>
          <IconButton
            icon={Edit3}
            label={editing ? "Stop editing" : "Edit before publishing"}
            disabled={busy}
            onClick={() => setEditing(!editing)}
          />
        </div>

        <p className="upm-approve__summary">
          {pending.summary ||
            `The agent drafted content for ${pending.destinations.length} destination${pending.destinations.length === 1 ? "" : "s"}. Review, pick which to publish, then approve.`}
        </p>
        {pending.destinations.some(d => d !== "wordpress") && (
          <p className="upm-approve__summary">
            Hashnode and LinkedIn go live, so they publish only a draft version written in the content studio,
            tested in the wind tunnel and approved. Text drafted here will be refused there; a WordPress draft
            goes through as before.
          </p>
        )}

        <div className="upm-adv-eyebrow">
          Destinations ({selectedCount} of {pending.destinations.length} selected)
        </div>

        <div className="upm-approve__dests">
          {pending.destinations.map(dest => {
            const meta = DEST_META[dest];
            const isIncluded = !!included[dest];
            return (
              <div
                key={dest}
                className="upm-approve__dest"
                data-included={isIncluded}
                data-active={activeTab === dest}
              >
                {/* Control one: include this destination in the publish. */}
                <Checkbox
                  checked={isIncluded}
                  label={isIncluded ? `Exclude ${meta.label}` : `Include ${meta.label}`}
                  labelHidden
                  state={busy ? "disabled" : undefined}
                  onChange={() => setIncluded(prev => ({ ...prev, [dest]: !prev[dest] }))}
                />
                {/* Control two: show me this one. NOT the same question. */}
                <button
                  type="button"
                  className="upm-approve__desttab"
                  disabled={busy}
                  aria-pressed={activeTab === dest}
                  onClick={() => setActiveTab(dest)}
                >
                  <DestinationMark id={dest} />
                  <span style={{ minWidth: 0 }}>
                    <span className="upm-approve__destname" style={{ display: "block" }}>{meta.label}</span>
                    <span className="upm-approve__destmeta">{meta.blurb}</span>
                  </span>
                </button>
              </div>
            );
          })}
        </div>

        <div className="upm-approve__editor" data-included={!!included[activeTab]}>
          <DestinationEditor
            destination={activeTab}
            content={edited[activeTab] || {}}
            onChange={(field, value) => updateField(activeTab, field, value)}
            editing={editing && !busy}
          />
        </div>

        <div className="upm-approve__actions">
          <Button icon={Rocket} onClick={handlePublish} disabled={busy || selectedCount === 0}>
            {cta}
          </Button>
          <Button variant="secondary" icon={X} onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <span className="upm-approve__promise">Nothing leaves Upmode without your approval.</span>
        </div>
      </div>
    </div>
  );
}

/* ─── per-destination editors ─────────────────────────────────────────────── */

function DestinationEditor({
  destination, content, onChange, editing,
}: {
  destination: PublishDestination;
  content: Record<string, any>;
  onChange: (field: string, value: any) => void;
  editing: boolean;
}) {
  if (destination === "hashnode") return <HashnodePreview content={content} onChange={onChange} editing={editing} />;
  if (destination === "wordpress") return <WordPressPreview content={content} onChange={onChange} editing={editing} />;
  if (destination === "linkedin") return <LinkedInPreview content={content} onChange={onChange} editing={editing} />;
  return null;
}

function HashnodePreview({ content, onChange, editing }: EditorProps) {
  const title = content.title || "";
  const markdown = content.content_markdown || "";
  const tags = Array.isArray(content.tags) ? content.tags : [];

  return (
    <>
      <FieldLabel>Title</FieldLabel>
      {editing
        ? <TextInput value={title} maxLength={250} aria-label="Hashnode title" onChange={e => onChange("title", e.target.value)} />
        : <div className="upm-approve__readtitle">{title || <Absent>No title</Absent>}</div>}

      <FieldLabel>Content (Markdown)</FieldLabel>
      {editing
        ? <Textarea value={markdown} rows={12} aria-label="Hashnode content" onChange={e => onChange("content_markdown", e.target.value)} />
        : <div className="upm-approve__readbody">{markdown || <Absent>Nothing drafted</Absent>}</div>}

      {tags.length > 0 && (
        <>
          <FieldLabel>Tags</FieldLabel>
          <div className="upm-approve__tags">
            {tags.slice(0, 5).map((t: string, i: number) => (
              <span key={i} className="upm-approve__tag">{t}</span>
            ))}
          </div>
        </>
      )}

      <CharCount current={markdown.length} />
    </>
  );
}

function WordPressPreview({ content, onChange, editing }: EditorProps) {
  const title = content.title || "";
  const html = content.content_html || "";
  const excerpt = content.excerpt || "";

  return (
    <>
      <FieldLabel>Title</FieldLabel>
      {editing
        ? <TextInput value={title} maxLength={200} aria-label="WordPress title" onChange={e => onChange("title", e.target.value)} />
        : <div className="upm-approve__readtitle">{title || <Absent>No title</Absent>}</div>}

      <FieldLabel>Content (HTML)</FieldLabel>
      {editing
        ? <Textarea value={html} rows={12} aria-label="WordPress content" onChange={e => onChange("content_html", e.target.value)} />
        : <div className="upm-approve__readbody">{html || <Absent>Nothing drafted</Absent>}</div>}

      {excerpt && (
        <>
          <FieldLabel>Excerpt</FieldLabel>
          {editing
            ? <TextInput value={excerpt} maxLength={300} aria-label="WordPress excerpt" onChange={e => onChange("excerpt", e.target.value)} />
            : <div className="upm-approve__readbody">{excerpt}</div>}
        </>
      )}

      <CharCount current={html.length} />
    </>
  );
}

function LinkedInPreview({ content, onChange, editing }: EditorProps) {
  const text = content.text || "";

  return (
    <>
      <FieldLabel>Post text</FieldLabel>
      {editing
        ? <Textarea value={text} rows={10} aria-label="LinkedIn post text" onChange={e => onChange("text", e.target.value)} />
        : <div className="upm-approve__readbody">{text || <Absent>Nothing drafted</Absent>}</div>}

      {/* Both thresholds are DERIVED from the same length the count prints, so
          the warning and the number can never disagree. */}
      <CharCount current={text.length} linkedin />
    </>
  );
}

/* ─── small parts ─────────────────────────────────────────────────────────── */

interface EditorProps {
  content: Record<string, any>;
  onChange: (field: string, value: any) => void;
  editing: boolean;
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div className="upm-adv-eyebrow">{children}</div>;
}

/** An absent field says what is missing, in words. Never "(empty)". */
function Absent({ children }: { children: React.ReactNode }) {
  return <span className="upm-approve__absent">{children}</span>;
}

function CharCount({ current, linkedin }: { current: number; linkedin?: boolean }) {
  const hard = linkedin && current > LINKEDIN_HARD;
  const soft = linkedin && !hard && current > LINKEDIN_SOFT;
  return (
    <div className="upm-approve__chars">
      <span>{current.toLocaleString()} characters</span>
      {hard && (
        <span className="upm-approve__limit upm-approve__limit--hard">
          <Icon icon={AlertTriangle} size="sm" />
          Over the {LINKEDIN_HARD.toLocaleString()} character limit, LinkedIn will reject this
        </span>
      )}
      {soft && (
        <span className="upm-approve__limit upm-approve__limit--soft">
          <Icon icon={AlertTriangle} size="sm" />
          Over {LINKEDIN_SOFT.toLocaleString()} characters, LinkedIn will truncate it in the feed preview
        </span>
      )}
      {linkedin && !hard && !soft && current > 0 && (
        <span className="upm-approve__limit" style={{ color: "var(--st-ok-t)" }}>
          <Icon icon={Check} size="sm" />
          Within LinkedIn's feed limit
        </span>
      )}
    </div>
  );
}
